import React, { useState } from 'react'
import './master-data-manager.scss'
import { useDispatch, useSelector } from 'react-redux';
import { getAllAdvices, getAllLabTests } from '../../../store/slices/prescriptionSlice';
import { del, put } from '../../../utils/fetchAPI';
import { showSuccessToast } from '../../../utils/toaster-alert';
import CustomModal from '../../common/modal/modal';
import GroupManager from '../../common/group-manager/group-manager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faTrash } from '@fortawesome/free-solid-svg-icons';

// One entry per doctor-owned master list that can be managed (edit/delete)
// from a section header via the gear icon.
const ENTITY_CONFIG = {
    advice: {
        title: "Manage Advice",
        groupsTitle: "Advice Groups",
        confirmTitle: "Delete Advice",
        confirmText: "Are you sure you want to delete this advice?",
        updatedMsg: "Advice updated",
        deletedMsg: "Advice deleted successfully",
        emptyText: "No advice saved yet.",
        textKey: "details",
        listKey: "adviceList",
        fetchList: (doctorId) => getAllAdvices({ doctorId, search: '' }),
        updateUrl: "doctor/advice/update",
        updatePayload: (item, text, doctorId) => ({ adviceId: item._id, details: text, doctorId: doctorId }),
        deleteUrl: (item, doctorId) => `/doctor/advice/delete/${item._id}?doctorId=${doctorId}`,
    },
    labTest: {
        title: "Manage Investigations",
        groupsTitle: "Investigation Groups",
        confirmTitle: "Delete Investigation",
        confirmText: "Are you sure you want to delete this investigation?",
        updatedMsg: "Investigation updated",
        deletedMsg: "Investigation deleted successfully",
        emptyText: "No investigation saved yet.",
        textKey: "name",
        listKey: "labTestList",
        fetchList: (doctorId) => getAllLabTests({ doctorId, search: '' }),
        updateUrl: "doctor/lab-test/update",
        updatePayload: (item, text, doctorId) => ({ labTestId: item._id, name: text, doctorId: doctorId }),
        deleteUrl: (item, doctorId) => `/doctor/lab-test/delete/${item._id}?doctorId=${doctorId}`,
    },
};

const MasterDataManager = ({ entity, doctorId }) => {
    const config = ENTITY_CONFIG[entity];
    const list = useSelector((state) => state.prescription[config.listKey]);
    const dispatch = useDispatch();
    const [showManager, setShowManager] = useState(false);
    const [activeTab, setActiveTab] = useState('manage'); // 'manage' | 'groups'
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [drafts, setDrafts] = useState({}); // itemId -> text being edited

    const refreshList = () => {
        if (doctorId) dispatch(config.fetchList(doctorId));
    };

    const openManager = () => {
        setDrafts({});
        setActiveTab('manage');
        refreshList();
        setShowManager(true);
    };

    const clearDraft = (itemId) => {
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    };

    const saveItem = async (item) => {
        const draft = drafts[item?._id];
        if (draft === undefined) return; // untouched
        const text = draft.trim();
        if (!text || text === item?.[config.textKey]) {
            clearDraft(item._id); // empty or unchanged -> revert
            return;
        }
        await put(config.updateUrl, config.updatePayload(item, text, doctorId))
            .then((res) => {
                if (res?.status === 200 && res?.data?._id) {
                    showSuccessToast(config.updatedMsg);
                    refreshList();
                }
            })
            .catch(() => { })
            .finally(() => clearDraft(item._id));
    };

    const deleteItem = async () => {
        if (!deleteTarget?._id) return;
        setIsDeleting(true);
        await del(config.deleteUrl(deleteTarget, doctorId))
            .then((res) => {
                if (res?.status === 200) {
                    setDeleteTarget(null);
                    showSuccessToast(config.deletedMsg);
                    refreshList();
                }
            })
            .catch(() => { })
            .finally(() => setIsDeleting(false));
    };

    if (!config) return null;

    return (
        <>
            <button type="button" className="mdm-gear" title={config.title} onClick={openManager}>
                <FontAwesomeIcon icon={faGear} />
            </button>

            {/* ── Manager: manage list / groups tabs ────────────────────────── */}
            <CustomModal size="lg" show={showManager} setShow={setShowManager} isHeaderHide={true}>
                <div className="mdm-modal">
                    <div className="mdm-modal__head">
                        <h2 className="mdm-modal__title">{activeTab === 'groups' ? config.groupsTitle : config.title}</h2>
                        <button className="mdm-modal__close" onClick={() => setShowManager(false)}>Close</button>
                    </div>
                    <div className="groups-settings__tabs">
                        <button
                            type="button"
                            className={`groups-settings__tab ${activeTab === 'manage' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('manage')}
                        >
                            Manage
                        </button>
                        <button
                            type="button"
                            className={`groups-settings__tab ${activeTab === 'groups' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('groups')}
                        >
                            Groups
                        </button>
                    </div>
                    {activeTab === 'manage' ? (
                        <>
                            <p className="mdm-modal__hint">Edit a text and click outside the field to save it.</p>
                            <div className="mdm-modal__body">
                                {list?.length > 0 ? list.map((item) => (
                                    <div key={item?._id} className="mdm-row">
                                        <textarea
                                            className="mdm-row__text"
                                            rows={2}
                                            placeholder={config.title}
                                            value={drafts[item?._id] !== undefined ? drafts[item._id] : (item?.[config.textKey] || "")}
                                            onChange={(e) => setDrafts((prev) => ({ ...prev, [item._id]: e.target.value }))}
                                            onBlur={() => saveItem(item)}
                                        />
                                        <button className="mdm-row__del" title="Delete" onClick={() => setDeleteTarget(item)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                )) : <p className="mdm-modal__empty">{config.emptyText}</p>}
                            </div>
                        </>
                    ) : (
                        <GroupManager doctorId={doctorId} type={entity} />
                    )}
                </div>
            </CustomModal>

            {/* ── Delete confirmation ───────────────────────────────────────── */}
            <CustomModal size="sm" show={!!deleteTarget} isHeaderHide={true}>
                <div className="mdm-modal">
                    <p className="mdm-modal__title">{config.confirmTitle}</p>
                    <p className="mdm-modal__text">{config.confirmText}</p>
                    <textarea className="mdm-row__text mdm-modal__preview" rows={2} disabled readOnly value={deleteTarget?.[config.textKey] || ""} />
                    <div className="mdm-modal__footer">
                        <button className="mdm-modal__btn mdm-modal__btn--danger" disabled={isDeleting} onClick={deleteItem}>
                            {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                        <button className="mdm-modal__btn" disabled={isDeleting} onClick={() => setDeleteTarget(null)}>Cancel</button>
                    </div>
                </div>
            </CustomModal>
        </>
    )
}

export default MasterDataManager
